import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { FileText, Users, Package, ClipboardList, FlaskConical } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { API } from "@/config";

export const Dashboard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalCustomers: 0,
    totalProducts: 0,
    totalRevenue: 0,
    totalPaid: 0,
    totalDue: 0
  });
  const [notifications, setNotifications] = useState({
    notifications: [],
    total_count: 0,
    overdue_count: 0,
    due_soon_count: 0
  });

  useEffect(() => {
    fetchStats();
    fetchNotifications();
  }, []);

  const fetchStats = async () => {
    try {
      const [invoices, customers, products, financial] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/products`),
        axios.get(`${API}/stats/financial`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStats({
        totalInvoices: invoices.data.length,
        totalCustomers: customers.data.length,
        totalProducts: products.data.length,
        totalRevenue: financial.data.total_revenue,
        totalPaid: financial.data.total_paid,
        totalDue: financial.data.total_due
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/due-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" data-testid="dashboard-title">Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="hover:shadow-lg transition-shadow" data-testid="invoices-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
            <FileText className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="total-invoices">{stats.totalInvoices}</div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow" data-testid="customers-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
            <Users className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="total-customers">{stats.totalCustomers}</div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow" data-testid="products-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Products</CardTitle>
            <Package className="h-5 w-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="total-products">{stats.totalProducts}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-500" data-testid="revenue-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <FileText className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600" data-testid="total-revenue">${stats.totalRevenue.toFixed(2)}</div>
            <p className="text-xs text-gray-600 mt-1">All invoices total</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-green-500" data-testid="paid-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
            <FileText className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600" data-testid="total-paid">${stats.totalPaid.toFixed(2)}</div>
            <p className="text-xs text-gray-600 mt-1">Payments received</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-red-500" data-testid="due-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Amount Due</CardTitle>
            <FileText className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600" data-testid="total-due">${stats.totalDue.toFixed(2)}</div>
            <p className="text-xs text-gray-600 mt-1">Outstanding balance</p>
          </CardContent>
        </Card>
      </div>

      {notifications.total_count > 0 && (
        <Card className="border-l-4 border-l-orange-500" data-testid="notifications-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
              </span>
              Payment Notifications
              {notifications.overdue_count > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                  {notifications.overdue_count} Overdue
                </span>
              )}
              {notifications.due_soon_count > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                  {notifications.due_soon_count} Due Soon
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {notifications.notifications.slice(0, 5).map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                    notification.status === 'overdue' ? 'bg-red-50 border-red-200' :
                    notification.status === 'due_soon' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-blue-50 border-blue-200'
                  }`}
                  onClick={() => navigate(`/invoices/${notification.id}`)}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{notification.invoice_number}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          notification.status === 'overdue' ? 'bg-red-200 text-red-800' :
                          notification.status === 'due_soon' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-blue-200 text-blue-800'
                        }`}>
                          {notification.status === 'overdue' ? 'OVERDUE' :
                           notification.status === 'due_soon' ? 'DUE SOON' : 'UPCOMING'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-700">{notification.customer_name}</div>
                      <div className="text-sm mt-1">{notification.message}</div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="font-bold text-lg">${notification.total.toFixed(2)}</div>
                      <div className="text-xs text-gray-600">
                        Due: {new Date(notification.due_date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {notifications.total_count > 5 && (
              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => navigate('/invoices')}
                data-testid="view-all-notifications-btn"
              >
                View All {notifications.total_count} Notifications
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/invoices')} data-testid="create-invoice-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Invoices
            </CardTitle>
            <CardDescription>Create and manage invoices</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/estimates')} data-testid="manage-estimates-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-6 w-6" />
              Estimates
            </CardTitle>
            <CardDescription>Send estimates to customers</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/trials')} data-testid="manage-trials-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="h-6 w-6" />
              Demo Trials
            </CardTitle>
            <CardDescription>Device trial agreements</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/customers')} data-testid="manage-customers-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-6 w-6" />
              Customers
            </CardTitle>
            <CardDescription>Manage customer information</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/products')} data-testid="manage-products-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-6 w-6" />
              Products
            </CardTitle>
            <CardDescription>Products and services</CardDescription>
          </CardHeader>
        </Card>
      </div>
    </div>
  );
};
